import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { ThemeToggle } from "@fleet-os/ui";
import "./Sidebar.css";

function Sidebar({
  categories = [],
  activeCategory,
  onCategoryClick,
  onAddAppClick,
  onAddSectionClick,
  onLogout,
  userName = "Daniel Radcliffe",
  userRole = "Admin",
  userEmail = ""
}) {
  const [dashboardExpanded, setDashboardExpanded] = useState(true);
  const [navSearch, setNavSearch] = useState("");
  const navigate = useNavigate();

  const initials = userName
    ? userName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "DR";

  // Filter categories shown in sidebar sub-list if search is used
  const filteredCategories = categories.filter((cat) =>
    cat.toLowerCase().includes(navSearch.toLowerCase())
  );

  return (
    <aside className="sidebar">
      {/* Brand Header */}
      <div className="sidebar__brand">
        <div className="sidebar__logo">
          <span className="sidebar__logo-dot" />
          <span className="sidebar__logo-dot" />
          <span className="sidebar__logo-dot" />
          <span className="sidebar__logo-dot" />
        </div>
        <span className="sidebar__brand-name">Fleet OS</span>
      </div>

      {/* Sidebar Search */}
      <div className="sidebar__search-container">
        <div className="sidebar__search-wrapper">
          <svg className="sidebar__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            className="sidebar__search-input"
            placeholder="Search"
            value={navSearch}
            onChange={(e) => setNavSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Navigation Links */}
      <nav className="sidebar__nav">
        <div className="sidebar__menu-group">
          {/* Dashboard parent link */}
          <button
            className={`sidebar__nav-item sidebar__nav-item--parent ${dashboardExpanded ? "expanded" : ""}`}
            onClick={() => setDashboardExpanded(!dashboardExpanded)}
          >
            <div className="sidebar__nav-item-left">
              <svg className="sidebar__nav-item-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="7" height="9" rx="1" stroke="currentColor" strokeWidth="2"/>
                <rect x="14" y="3" width="7" height="5" rx="1" stroke="currentColor" strokeWidth="2"/>
                <rect x="14" y="12" width="7" height="9" rx="1" stroke="currentColor" strokeWidth="2"/>
                <rect x="3" y="16" width="7" height="5" rx="1" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <span>Dashboard</span>
            </div>
            <svg
              className={`sidebar__arrow ${dashboardExpanded ? "rotated" : ""}`}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* Sub-categories */}
          {dashboardExpanded && (
            <div className="sidebar__sub-nav">
              {filteredCategories.map((cat, idx) => (
                <button
                  key={idx}
                  className={`sidebar__sub-nav-item ${activeCategory === cat ? "active" : ""}`}
                  onClick={() => onCategoryClick(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Analytics link */}
        <button className="sidebar__nav-item">
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-item-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="18" y1="20" x2="18" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="12" y1="20" x2="12" y2="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="6" y1="20" x2="6" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span>Analytics</span>
          </div>
        </button>

        {/* Notifications link */}
        <button className="sidebar__nav-item">
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-item-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="currentColor" strokeWidth="2"/>
              <path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <span>Notifications</span>
          </div>
        </button>

        {/* IT Support Desk link */}
        <button className="sidebar__nav-item sidebar__nav-item--isd" onClick={() => navigate('/isd')}>
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-item-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" stroke="currentColor" strokeWidth="2"/>
              <path d="M13 5v2" stroke="currentColor" strokeWidth="2"/>
              <path d="M13 17v2" stroke="currentColor" strokeWidth="2"/>
              <path d="M13 11v2" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <span>IT Support Desk</span>
          </div>
          <span style={{ fontSize: '0.6rem', background: 'rgba(16,185,129,0.12)', color: '#10b981', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>NEW</span>
        </button>

        {/* Customer Support Desk link */}
        <button className="sidebar__nav-item sidebar__nav-item--csd" onClick={() => navigate('/csd')}>
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-item-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 18v-6a9 9 0 0 1 18 0v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <span>Customer Support</span>
          </div>
          <span style={{ fontSize: '0.6rem', background: 'rgba(20,184,166,0.12)', color: '#14b8a6', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>NEW</span>
        </button>

        {/* DPT Dashboard Link */}
        <button className={`sidebar__nav-item ${location.pathname.startsWith('/dpt') ? 'active' : ''}`} onClick={() => navigate('/dpt')}>
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-item-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
              <line x1="8" y1="21" x2="16" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="12" y1="17" x2="12" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span>DPT Dashboard</span>
          </div>
        </button>

        {/* Raabta CSM Dashboard Link */}
        <button className={`sidebar__nav-item ${location.pathname.startsWith('/csm') ? 'active' : ''}`} onClick={() => navigate('/csm')}>
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-item-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Raabta CSM</span>
          </div>
          <span style={{ fontSize: '0.6rem', background: 'rgba(139,92,246,0.12)', color: '#8b5cf6', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>NEW</span>
        </button>

        {/* Hawkeye Dispatch Wallboard Link */}
        <button className={`sidebar__nav-item ${location.pathname.startsWith('/hawkeye') ? 'active' : ''}`} onClick={() => navigate('/hawkeye')}>
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-item-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <span>Shaheen</span>
          </div>
          <span style={{ fontSize: '0.6rem', background: 'rgba(245,158,11,0.12)', color: '#f59e0b', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>NEW</span>
        </button>

        {/* Engineering Boards Link */}
        <button className={`sidebar__nav-item ${location.pathname.startsWith('/boards') ? 'active' : ''}`} onClick={() => navigate('/boards')}>
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-item-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="7" height="18" rx="1" stroke="currentColor" strokeWidth="2"/>
              <rect x="14" y="3" width="7" height="11" rx="1" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <span>Engineering Boards</span>
          </div>
          <span style={{ fontSize: '0.6rem', background: 'rgba(99,102,241,0.12)', color: '#6366f1', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>NEW</span>
        </button>

        {/* City Operations Link */}
        <button className={`sidebar__nav-item ${location.pathname.startsWith('/city-ops') ? 'active' : ''}`} onClick={() => navigate('/city-ops')}>
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-item-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 21h18M6 21V7l6-4 6 4v14M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>City Operations</span>
          </div>
          <span style={{ fontSize: '0.6rem', background: 'rgba(77,145,69,0.15)', color: '#4d9145', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>NEW</span>
        </button>

        {/* Process Governance Link */}
        <button className={`sidebar__nav-item ${location.pathname.startsWith('/process-governance') ? 'active' : ''}`} onClick={() => navigate('/process-governance')}>
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-item-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Process Governance</span>
          </div>
          <span style={{ fontSize: '0.6rem', background: 'rgba(236,72,153,0.12)', color: '#ec4899', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>NEW</span>
        </button>

        {/* Training Dashboard Link */}
        <button className={`sidebar__nav-item ${location.pathname.startsWith('/training') ? 'active' : ''}`} onClick={() => navigate('/training')}>
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-item-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 9L12 4 2 9l10 5 10-5zm0 0v6M12 21v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Training Dashboard</span>
          </div>
          <span style={{ fontSize: '0.6rem', background: 'rgba(56,189,248,0.12)', color: '#38bdf8', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>NEW</span>
        </button>

        {/* Opal (EOS / Level-10) Link */}
        <button className={`sidebar__nav-item ${location.pathname.startsWith('/opal') ? 'active' : ''}`} onClick={() => navigate('/opal')}>
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-item-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2l9 6-9 6-9-6 9-6zM3 14l9 6 9-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Opal</span>
          </div>
          <span style={{ fontSize: '0.6rem', background: 'rgba(16,185,129,0.12)', color: '#10b981', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>NEW</span>
        </button>

        {/* ABS Calibration Link */}
        <button className={`sidebar__nav-item ${location.pathname.startsWith('/abs') ? 'active' : ''}`} onClick={() => navigate('/abs')}>
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-item-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>ABS Calibration</span>
          </div>
          <span style={{ fontSize: '0.6rem', background: 'rgba(245,158,11,0.12)', color: '#f59e0b', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>NEW</span>
        </button>

        {/* Vehicle Management (Qafila / VMS) Link */}
        <button className={`sidebar__nav-item ${location.pathname.startsWith('/vms') ? 'active' : ''}`} onClick={() => navigate('/vms')}>
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-item-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 13l2-5a2 2 0 011.9-1.4h10.2A2 2 0 0119 8l2 5m-18 0v5a1 1 0 001 1h1a1 1 0 001-1v-1h12v1a1 1 0 001 1h1a1 1 0 001-1v-5m-18 0h18M6.5 16.5h.01M17.5 16.5h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Vehicle Management</span>
          </div>
          <span style={{ fontSize: '0.6rem', background: 'rgba(6,182,212,0.14)', color: '#06b6d4', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>NEW</span>
        </button>

        {/* Ninety Insights (Ninety.io / EOS analytics) Link */}
        <button className={`sidebar__nav-item ${location.pathname.startsWith('/ninety') ? 'active' : ''}`} onClick={() => navigate('/ninety')}>
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-item-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 3v18h18M7 14l3-3 3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Ninety Insights</span>
          </div>
          <span style={{ fontSize: '0.6rem', background: 'rgba(139,92,246,0.14)', color: '#8b5cf6', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>NEW</span>
        </button>
      </nav>

      {/* Buttons */}
      <div className="sidebar__actions">
        <button className="sidebar__btn sidebar__btn--primary" onClick={onAddAppClick}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          Add Apps
        </button>
        <button className="sidebar__btn sidebar__btn--secondary" onClick={onAddSectionClick}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          Add Section
        </button>
      </div>

      {/* Bottom Profile and Footer */}
      <div className="sidebar__footer">
        <ThemeToggle />
        <div className="sidebar__profile-wrapper">
          <div className="sidebar__avatar">
            {initials}
          </div>
          <div className="sidebar__profile-info">
            <span className="sidebar__profile-name">{userName}</span>
            <span className="sidebar__profile-email" style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: '1.2' }}>{userEmail}</span>
            <span className="sidebar__profile-badge" style={{ marginTop: '4px' }}>{userRole}</span>
          </div>
        </div>

        <div className="sidebar__footer-actions">
          <button className="sidebar__footer-btn" onClick={() => navigate('/settings')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="2"/>
            </svg>
            Settings
          </button>
          <button className="sidebar__footer-btn sidebar__footer-btn--danger" onClick={onLogout}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2"/>
              <polyline points="16 17 21 12 16 7" stroke="currentColor" strokeWidth="2"/>
              <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2"/>
            </svg>
            Log out
          </button>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
